// Base entity class
// Everything in the game that moves and can be drawn extends this

import { Vec2, VectorShape, BoundingBox } from '../utils/types';
import { VectorRenderer } from '../graphics/VectorRenderer';

export abstract class Entity {
  protected position: Vec2;
  protected velocity: Vec2;
  protected rotation: number; // radians
  protected scale: number;
  protected shape: VectorShape;
  protected active: boolean;
  protected color: string;

  constructor(x: number, y: number) {
    this.position = { x, y };
    this.velocity = { x: 0, y: 0 };
    this.rotation = 0;
    this.scale = 1;
    this.shape = { points: [], closed: true };
    this.active = true;
    this.color = '#0f0';
  }

  abstract update(dt: number): void;

  render(renderer: VectorRenderer): void {
    if (!this.active) return;
    renderer.drawShape(this.shape, this.position, this.rotation, this.scale, this.color);
  }

  getPosition(): Vec2 {
    return { ...this.position };
  }

  setPosition(x: number, y: number): void {
    this.position.x = x;
    this.position.y = y;
  }

  getVelocity(): Vec2 {
    return { ...this.velocity };
  }

  setVelocity(vx: number, vy: number): void {
    this.velocity.x = vx;
    this.velocity.y = vy;
  }

  isActive(): boolean {
    return this.active;
  }

  destroy(): void {
    this.active = false;
  }

  // Override in subclasses for more accurate bounds
  getBoundingBox(): BoundingBox {
    // Default: use shape points to calculate bounds
    if (this.shape.points.length === 0) {
      return {
        minX: this.position.x,
        minY: this.position.y,
        maxX: this.position.x,
        maxY: this.position.y,
      };
    }

    let minX = Infinity,
      minY = Infinity;
    let maxX = -Infinity,
      maxY = -Infinity;

    for (const point of this.shape.points) {
      const x = this.position.x + point.x * this.scale;
      const y = this.position.y + point.y * this.scale;
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }

    return { minX, minY, maxX, maxY };
  }
}
